import React from "react";
import { LegalPage, H2, P, UL } from "./LegalPage";

export const PrivacyPolicy = () => (
  <LegalPage
    eyebrow="Legal"
    title="Privacy Policy"
    description="How Kerala Speaks collects, uses, and protects citizen data. We track patterns, not identities."
    canonical="/privacy"
    lastUpdated="May 2026"
    testId="privacy-page"
  >
    <P>
      Kerala Speaks is built around a simple promise: <strong>we track patterns, not identities.</strong>
      This policy explains exactly what we collect, why we collect it, how it's used, and how it's protected.
    </P>

    <H2>1. What we collect</H2>
    <UL>
      <li><strong>Public submissions.</strong> Messages, votes, and follow-up comments you choose to post on the platform.</li>
      <li><strong>Optional demographic profile.</strong> Age group, district, occupation, and (optionally) gender — only if you fill the post-vote form. Skip is always an option.</li>
      <li><strong>Optional email address.</strong> Only if you opt-in to be notified about future civic questions.</li>
      <li><strong>Technical signals.</strong> A pseudonymous voter id (a random string stored in your browser), IP address (hashed before storage for spam protection), browser type, and the page you came from.</li>
    </UL>

    <H2>2. Why we collect it</H2>
    <UL>
      <li><strong>Civic insight.</strong> Aggregating opinions by district, age group, and occupation helps Kerala understand its own priorities.</li>
      <li><strong>Anti-spam / anti-abuse.</strong> Hashed IPs and the voter id let us prevent the same person from voting twice without revealing who they are.</li>
      <li><strong>Future communication.</strong> If you opt-in, we use your email only to notify you about new civic polls.</li>
    </UL>

    <H2>3. What we do NOT do</H2>
    <UL>
      <li>We do <strong>not sell, rent, or share personal data</strong> with advertisers, brokers, or third parties.</li>
      <li>We do <strong>not use citizen data for political targeting</strong>, profiling, or advertising of any kind.</li>
      <li>We do <strong>not display your identity publicly.</strong> Demographic data is only ever published as aggregate counts (e.g. "62% of 18–24 year olds in Kollam supported option A").</li>
    </UL>

    <H2>4. How long we keep it</H2>
    <P>
      Submissions and aggregate insights are kept indefinitely as part of Kerala's civic record. Email addresses are kept only for as long as you choose to be subscribed — unsubscribe links are provided in every email and you can also email us to be removed immediately.
    </P>

    <H2>5. How it's protected</H2>
    <UL>
      <li>All traffic is encrypted in transit (HTTPS).</li>
      <li>IPs are hashed before they touch storage — they cannot be reversed to reveal you.</li>
      <li>Admin access is gated behind authenticated, audit-logged sign-in.</li>
      <li>The voter id stored on your device is a random string with no link to your real identity.</li>
    </UL>

    <H2>6. Cookies & local storage</H2>
    <P>
      We use one functional cookie (<code>ks_voter</code>) and one local-storage entry (<code>ks_voter_id_v1</code>) to keep your vote attached to you across page reloads. We do <strong>not</strong> use third-party advertising or tracking cookies. Optional Google Analytics is used in aggregate (no personal identifiers).
    </P>

    <H2>7. Your rights</H2>
    <UL>
      <li>You can ask us to delete any of your contributions — email <a className="underline" href="mailto:keralaspeaks7@gmail.com">keralaspeaks7@gmail.com</a> from the email you registered with, or attach enough context to identify your submission.</li>
      <li>You can unsubscribe from emails any time via the link in any email or the same address above.</li>
      <li>You can request a copy of the data we hold about you.</li>
    </UL>

    <H2>8. Children</H2>
    <P>
      Kerala Speaks is intended for users aged 13 and over. We do not knowingly collect data from anyone under 13.
    </P>

    <H2>9. Changes to this policy</H2>
    <P>
      If we ever change how data is handled, the "Last updated" date above will change and we'll post a brief notice on the homepage. Material changes will be flagged before they take effect.
    </P>

    <H2>10. Contact</H2>
    <P>
      Questions or requests? Write to <a className="underline" href="mailto:keralaspeaks7@gmail.com">keralaspeaks7@gmail.com</a>.
    </P>
  </LegalPage>
);

export default PrivacyPolicy;
